import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { planningPeriodsApi } from '@/utils/api';
import type { PlanningPeriod } from '@/types';

interface PlanningPeriodsState {
  periods: PlanningPeriod[];
  currentPeriod: PlanningPeriod | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: PlanningPeriodsState = {
  periods: [],
  currentPeriod: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

// Async thunks
export const fetchPlanningPeriods = createAsyncThunk(
  'planningPeriods/fetchAll',
  async (_, { rejectWithValue }) => {
    const response = await planningPeriodsApi.getAll();
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to fetch planning periods');
    }
    return response.data.planningPeriods as PlanningPeriod[];
  }
);

export const fetchPlanningPeriod = createAsyncThunk(
  'planningPeriods/fetchOne',
  async (id: string, { rejectWithValue }) => {
    const response = await planningPeriodsApi.getById(id);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to fetch planning period');
    }
    return response.data.planningPeriod as PlanningPeriod;
  }
);

export const createPlanningPeriod = createAsyncThunk(
  'planningPeriods/create',
  async (data: Partial<PlanningPeriod>, { rejectWithValue }) => {
    const response = await planningPeriodsApi.create(data);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to create planning period');
    }
    return response.data.planningPeriod as PlanningPeriod;
  }
);

export const updatePlanningPeriod = createAsyncThunk(
  'planningPeriods/update',
  async ({ id, data }: { id: string; data: Partial<PlanningPeriod> }, { rejectWithValue }) => {
    const response = await planningPeriodsApi.update(id, data);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to update planning period');
    }
    return response.data.planningPeriod as PlanningPeriod;
  }
);

export const deletePlanningPeriod = createAsyncThunk(
  'planningPeriods/delete',
  async (id: string, { rejectWithValue }) => {
    const response = await planningPeriodsApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete planning period');
    }
    return id;
  }
);

export const activatePlanningPeriod = createAsyncThunk(
  'planningPeriods/activate',
  async (id: string, { rejectWithValue }) => {
    const response = await planningPeriodsApi.activate(id);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to activate planning period');
    }
    return response.data.planningPeriod as PlanningPeriod;
  }
);

export const closePlanningPeriod = createAsyncThunk(
  'planningPeriods/close',
  async (id: string, { rejectWithValue }) => {
    const response = await planningPeriodsApi.close(id);
    if (!response.success || !response.data) {
      return rejectWithValue(response.error?.message || 'Failed to close planning period');
    }
    return response.data.planningPeriod as PlanningPeriod;
  }
);

const planningPeriodsSlice = createSlice({
  name: 'planningPeriods',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPeriod: (state) => {
      state.currentPeriod = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchPlanningPeriods.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlanningPeriods.fulfilled, (state, action: PayloadAction<PlanningPeriod[]>) => {
        state.isLoading = false;
        state.periods = action.payload;
        state.error = null;
      })
      .addCase(fetchPlanningPeriods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch one
      .addCase(fetchPlanningPeriod.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlanningPeriod.fulfilled, (state, action: PayloadAction<PlanningPeriod>) => {
        state.isLoading = false;
        state.currentPeriod = action.payload;
      })
      .addCase(fetchPlanningPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createPlanningPeriod.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPlanningPeriod.fulfilled, (state, action: PayloadAction<PlanningPeriod>) => {
        state.isCreating = false;
        state.periods.unshift(action.payload);
      })
      .addCase(createPlanningPeriod.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updatePlanningPeriod.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updatePlanningPeriod.fulfilled, (state, action: PayloadAction<PlanningPeriod>) => {
        state.isUpdating = false;
        const index = state.periods.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.periods[index] = action.payload;
        }
        if (state.currentPeriod?.id === action.payload.id) {
          state.currentPeriod = action.payload;
        }
      })
      .addCase(updatePlanningPeriod.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deletePlanningPeriod.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deletePlanningPeriod.fulfilled, (state, action: PayloadAction<string>) => {
        state.isDeleting = false;
        state.periods = state.periods.filter(p => p.id !== action.payload);
        if (state.currentPeriod?.id === action.payload) {
          state.currentPeriod = null;
        }
      })
      .addCase(deletePlanningPeriod.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Activate
      .addCase(activatePlanningPeriod.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(activatePlanningPeriod.fulfilled, (state, action: PayloadAction<PlanningPeriod>) => {
        state.isUpdating = false;
        // Update all periods - deactivate others, activate this one
        state.periods = state.periods.map(p => ({
          ...p,
          isActive: p.id === action.payload.id,
          status: p.id === action.payload.id ? action.payload.status : p.status
        }));
        if (state.currentPeriod?.id === action.payload.id) {
          state.currentPeriod = action.payload;
        }
      })
      .addCase(activatePlanningPeriod.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Close
      .addCase(closePlanningPeriod.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(closePlanningPeriod.fulfilled, (state, action: PayloadAction<PlanningPeriod>) => {
        state.isUpdating = false;
        const index = state.periods.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.periods[index] = action.payload;
        }
        if (state.currentPeriod?.id === action.payload.id) {
          state.currentPeriod = action.payload;
        }
      })
      .addCase(closePlanningPeriod.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentPeriod } = planningPeriodsSlice.actions;
export default planningPeriodsSlice.reducer;